import React, { useState, useEffect } from 'react';
import { doc, onSnapshot, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { COLORS } from '../theme';
import OnboardingQuestionsPage from './OnboardingQuestionsPage';

// Prompt #285: combines the 2 admin surfaces that previously lived
// separately — the "Guest Showcase Intro" headline flag (was a FlagCard
// at the bottom of FeatureFlagsPage.js) and the "Showcase Q's" question
// page (was its own nav entry, `/guest-showcase-questions`) — into one
// "Guest Preview" tab. `FlagCard`/`TextField`/`Toggle` below are
// deliberately duplicated from FeatureFlagsPage.js rather than extracted
// into a new shared module: they're small, private, purely
// presentational, and FeatureFlagsPage.js still needs them for its own
// remaining flags — introducing a shared-components module for this one
// reuse would be more architecture than the actual problem calls for.
// The question-management piece itself is NOT duplicated — this composes
// the existing parameterized OnboardingQuestionsPage component as-is,
// with the same docPath/title props it already supported.
//
// Prompt #288: the "Unauthenticated Browse Fields" section (4 toggles)
// relocated here from FeatureFlagsPage.js — a pure UI move, same
// config/features.unauthenticatedBrowseFields.{title,location,salary,
// companyName} schema, same nested-merge write pattern, same Toggle
// behavior. Clarence's framing: these are "guest" settings from an
// admin's point of view (this tab's actual scope, regardless of which
// HC-GUEST-BROWSE part originally shipped each piece — Part 1 for these
// toggles, Part 2/HC-GUEST-SHOWCASE-INTRO for the headline/questions
// above), not a revert of Prompt #285's finding that they're unrelated
// in feature *origin* — that finding was about feature scope, not about
// where an admin should go to find "guest" config day to day.

function Toggle({ checked, onChange, disabled }) {
  return (
    <div
      onClick={() => !disabled && onChange(!checked)}
      style={{
        width: '44px',
        height: '24px',
        borderRadius: '12px',
        backgroundColor: checked ? COLORS.accent : COLORS.border,
        position: 'relative',
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'background-color 0.2s',
        flexShrink: 0,
        opacity: disabled ? 0.5 : 1,
      }}
      title={disabled ? 'Read-only access' : undefined}
    >
      <div style={{
        position: 'absolute',
        top: '2px',
        left: checked ? '22px' : '2px',
        width: '20px',
        height: '20px',
        borderRadius: '50%',
        backgroundColor: '#fff',
        transition: 'left 0.2s',
        boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
      }} />
    </div>
  );
}

function TextField({ value, onSave, disabled, placeholder }) {
  const [draft, setDraft] = useState(value || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDraft(value || '');
  }, [value]);

  const dirty = draft !== (value || '');

  async function handleSave() {
    setSaving(true);
    try {
      await onSave(draft);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', minWidth: '320px' }}>
      <textarea
        value={draft}
        onChange={e => setDraft(e.target.value)}
        disabled={disabled}
        rows={2}
        placeholder={placeholder}
        style={{
          width: '100%',
          backgroundColor: COLORS.bg,
          border: `1px solid ${COLORS.border}`,
          borderRadius: '4px',
          padding: '8px 10px',
          color: COLORS.textPrimary,
          fontSize: '13px',
          resize: 'vertical',
          boxSizing: 'border-box',
          fontFamily: 'inherit',
        }}
      />
      <button
        onClick={handleSave}
        disabled={disabled || !dirty || saving}
        style={{
          alignSelf: 'flex-end',
          backgroundColor: dirty && !disabled ? COLORS.success : COLORS.border,
          color: dirty && !disabled ? 'white' : COLORS.textMuted,
          border: 'none',
          borderRadius: '4px',
          padding: '6px 14px',
          fontSize: '13px',
          fontWeight: '600',
          cursor: disabled || !dirty ? 'not-allowed' : 'pointer',
        }}
        title={disabled ? 'Read-only access' : undefined}
      >
        {saving ? 'Saving...' : 'Save'}
      </button>
    </div>
  );
}

function FlagCard({ label, description, control, updatedAt }) {
  return (
    <div style={{
      backgroundColor: COLORS.surface,
      border: `1px solid ${COLORS.border}`,
      borderRadius: '8px',
      padding: '24px',
      marginBottom: '16px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '24px',
    }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: '14px', fontWeight: '600', color: COLORS.textPrimary, marginBottom: '4px' }}>
          {label}
        </div>
        <div style={{ fontSize: '13px', color: COLORS.textSecondary }}>
          {description}
        </div>
        {updatedAt && (
          <div style={{ fontSize: '12px', color: COLORS.textMuted, marginTop: '8px' }}>
            Last updated: {updatedAt.toDate
              ? updatedAt.toDate().toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })
              : String(updatedAt)}
          </div>
        )}
      </div>
      <div style={{ flexShrink: 0 }}>
        {control}
      </div>
    </div>
  );
}

export default function GuestPreviewPage() {
  const { claims } = useAuth();
  const [flags, setFlags] = useState({
    guestIntroHeadline: '',
    unauthenticatedBrowseTitle: true,
    unauthenticatedBrowseLocation: true,
    unauthenticatedBrowseSalary: true,
    unauthenticatedBrowseCompanyName: false,
  });
  const [updatedAt, setUpdatedAt] = useState(null);
  const [loading, setLoading] = useState(true);

  const canWrite = claims.accessLevel === 'superadmin' || claims.accessLevel === 'operator';

  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'config', 'features'), snap => {
      if (snap.exists()) {
        const data = snap.data();
        setFlags({
          guestIntroHeadline: data.guestIntroHeadline || '',
          unauthenticatedBrowseTitle: data.unauthenticatedBrowseFields?.title !== false,
          unauthenticatedBrowseLocation: data.unauthenticatedBrowseFields?.location !== false,
          unauthenticatedBrowseSalary: data.unauthenticatedBrowseFields?.salary !== false,
          unauthenticatedBrowseCompanyName: data.unauthenticatedBrowseFields?.companyName === true,
        });
        setUpdatedAt(data.updatedAt || null);
      }
      setLoading(false);
    });
    return () => unsub();
  }, []);

  async function writeFlag(updates) {
    if (!canWrite) return;
    await setDoc(doc(db, 'config', 'features'), {
      ...updates,
      updatedAt: new Date(),
    }, { merge: true });
  }

  return (
    <div>
      <div style={{ fontSize: '13px', color: COLORS.textSecondary, marginBottom: '24px' }}>
        The pre-signup guest preview flow: a free-banner/language screen, an industry picker, and voice showcase questions — shown once per install before a guest reaches Browse Jobs.
        {!canWrite && (
          <span style={{ marginLeft: '12px', color: COLORS.warning, fontSize: '12px' }}>
            ⚠ Read-only access
          </span>
        )}
      </div>

      <div style={{ fontSize: '13px', fontWeight: '600', color: COLORS.textSecondary, marginBottom: '8px' }}>
        Screen 1 Headline
      </div>

      {loading ? (
        <div style={{ color: COLORS.textSecondary, fontSize: '14px', marginBottom: '24px' }}>Loading…</div>
      ) : (
        <FlagCard
          label="Screen 1 Headline"
          description="Prominent headline text on the guest preview's first screen."
          updatedAt={updatedAt}
          control={
            <TextField
              value={flags.guestIntroHeadline}
              onSave={val => writeFlag({ guestIntroHeadline: val })}
              disabled={!canWrite}
              placeholder="100% free — no subscriptions, no in-app purchases. Find a better job today."
            />
          }
        />
      )}

      <div style={{
        fontSize: '13px',
        fontWeight: '600',
        color: COLORS.textSecondary,
        marginTop: '32px',
        marginBottom: '16px',
        borderTop: `1px solid ${COLORS.border}`,
        paddingTop: '32px',
      }}>
        Preview Questions
      </div>

      <OnboardingQuestionsPage docPath="guestShowcaseQuestions" title="Guest Preview Questions" />

      <div style={{
        fontSize: '13px',
        fontWeight: '600',
        color: COLORS.textSecondary,
        marginTop: '32px',
        marginBottom: '8px',
        borderTop: `1px solid ${COLORS.border}`,
        paddingTop: '32px',
      }}>
        Unauthenticated Browse Fields
      </div>
      <div style={{ fontSize: '13px', color: COLORS.textSecondary, marginBottom: '16px' }}>
        Fields shown on the minimal job card guest (signed-out) users see on Browse Jobs. Independently toggleable — a hidden field never appears on the guest card, but the job itself is unaffected for signed-in users.
      </div>

      <FlagCard
        label="Guest Browse: Job Title"
        description="Show the job title on the guest job card."
        updatedAt={updatedAt}
        control={
          <Toggle
            checked={flags.unauthenticatedBrowseTitle}
            onChange={val => writeFlag({ unauthenticatedBrowseFields: { title: val } })}
            disabled={!canWrite}
          />
        }
      />

      <FlagCard
        label="Guest Browse: Location"
        description="Show the job location on the guest job card."
        updatedAt={updatedAt}
        control={
          <Toggle
            checked={flags.unauthenticatedBrowseLocation}
            onChange={val => writeFlag({ unauthenticatedBrowseFields: { location: val } })}
            disabled={!canWrite}
          />
        }
      />

      <FlagCard
        label="Guest Browse: Salary"
        description="Show the salary/pay range on the guest job card."
        updatedAt={updatedAt}
        control={
          <Toggle
            checked={flags.unauthenticatedBrowseSalary}
            onChange={val => writeFlag({ unauthenticatedBrowseFields: { salary: val } })}
            disabled={!canWrite}
          />
        }
      />

      <FlagCard
        label="Guest Browse: Company Name"
        description="Show the employer's company name on the guest job card. Off by default."
        updatedAt={updatedAt}
        control={
          <Toggle
            checked={flags.unauthenticatedBrowseCompanyName}
            onChange={val => writeFlag({ unauthenticatedBrowseFields: { companyName: val } })}
            disabled={!canWrite}
          />
        }
      />
    </div>
  );
}
